import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, Star, TicketPercent, PackageSearch, X, Truck, Check, Package, XCircle, Menu, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import Logo from "./Logo";
import CountdownTimer from "./CountdownTimer";
import { useCart } from "../context/CartContext";
import { scrollToId, PRODUCT, fmt, SAVE_PCT, API } from "../constants/site";

const NAV_LINKS = [
  { label: "Features", id: "features" },
  { label: "Gallery", id: "gallery" },
  { label: "Reviews", id: "reviews" },
  { label: "FAQ", id: "faq" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const { count, setIsOpen, add } = useCart();
  const navigate = useNavigate();
  const [trackOpen, setTrackOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [trackVal, setTrackVal] = useState("");
  const [trackOrder, setTrackOrder] = useState(null);
  const [trackLoading, setTrackLoading] = useState(false);
  const [trackErr, setTrackErr] = useState("");

  const trackSearch = (e) => {
    e.preventDefault();
    const v = trackVal.trim().toUpperCase();
    if (!v) return;
    setTrackLoading(true);
    setTrackErr("");
    fetch(`${API}/orders/${encodeURIComponent(v)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error())))
      .then((d) => setTrackOrder(d))
      .catch(() => {
        setTrackOrder(null);
        setTrackErr("We couldn't find that order number — please double-check it.");
      })
      .finally(() => setTrackLoading(false));
  };

  const closeTrack = () => {
    setTrackOpen(false);
    setTrackOrder(null);
    setTrackErr("");
    setTrackVal("");
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const buyNow = () => {
    add(1);
    toast.success("OmniMist added — taking you to checkout");
    navigate("/checkout");
  };

  return (
    <AnimatePresence>
      <motion.header
        initial={{ y: -70, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled
            ? "border-b border-charcoal-900/10 bg-oat-50/85 backdrop-blur-xl"
            : "border-b border-transparent bg-transparent"
        }`}
      >
            <div className="marquee overflow-hidden bg-gradient-to-r from-red-700 via-red-600 to-red-700 text-white" data-testid="announcement-bar">
              <div className="marquee-track flex w-max items-center gap-8 whitespace-nowrap py-2 text-xs font-semibold uppercase tracking-[0.14em]" style={{ animationDuration: "26s" }}>
                {[0, 1].map((copy) => (
                  <span key={copy} className="flex items-center gap-8" aria-hidden={copy === 1}>
                    <span>₹389 only — MRP ₹599 (Save 35%)</span>
                    <span className="h-1.5 w-1.5 rounded-full bg-gold" />
                    <span>Cash on Delivery available</span>
                    <span className="h-1.5 w-1.5 rounded-full bg-gold" />
                    <span>Free express shipping · ships in 24 hours</span>
                    <span className="h-1.5 w-1.5 rounded-full bg-gold" />
                    <CountdownTimer compact />
                    <span className="h-1.5 w-1.5 rounded-full bg-gold" />
                    <span>Easy returns — support on WhatsApp</span>
                    <span className="h-1.5 w-1.5 rounded-full bg-gold" />
                  </span>
                ))}
              </div>
            </div>
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 lg:px-10">
          <Logo />

          <nav className="hidden items-center gap-8 md:flex" data-testid="nav-links">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                onClick={() => scrollToId(l.id)}
                data-testid={`nav-link-${l.id}`}
                className="group relative text-[13px] font-medium uppercase tracking-[0.18em] text-charcoal-700 transition-colors hover:text-sage-600"
              >
                {l.label}
                <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-sage-500 transition-all duration-300 group-hover:w-full" />
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <div className="hidden items-center gap-1.5 rounded-full border border-charcoal-900/10 bg-white/60 px-3 py-1.5 sm:flex">
              <Star className="h-3.5 w-3.5 fill-gold text-gold" />
              <span className="text-xs font-medium text-charcoal-700">
                {PRODUCT.rating} · {PRODUCT.reviews} reviews
              </span>
            </div>

            <button
              onClick={buyNow}
              data-testid="nav-buy-now-btn"
              className="hidden rounded-full bg-charcoal-900 px-5 py-2.5 text-[13px] font-medium uppercase tracking-[0.14em] text-oat-100 transition-all duration-300 hover:bg-sage-600 md:block"
            >
              Buy Now
            </button>

            <button
              onClick={() => setMenuOpen(true)}
              data-testid="nav-menu-btn"
              aria-label="Open menu"
              className="flex h-11 w-11 items-center justify-center rounded-full border border-charcoal-900/15 bg-white/70 text-charcoal-900 transition-all duration-300 hover:border-sage-500 hover:text-sage-600 md:hidden"
            >
              <Menu className="h-[18px] w-[18px]" />
            </button>

            <button
              onClick={() => setTrackOpen(true)}
              data-testid="nav-track-btn"
              aria-label="Track your order"
              className="flex h-11 w-11 items-center justify-center rounded-full border border-charcoal-900/15 bg-white/70 text-charcoal-900 transition-all duration-300 hover:border-sage-500 hover:text-sage-600"
            >
              <PackageSearch className="h-[18px] w-[18px]" />
            </button>

            <button
              onClick={() => setIsOpen(true)}
              data-testid="nav-cart-btn"
              aria-label="Open cart"
              className="relative flex h-11 w-11 items-center justify-center rounded-full border border-charcoal-900/15 bg-white/70 text-charcoal-900 transition-all duration-300 hover:border-sage-500 hover:text-sage-600"
            >
              <ShoppingBag className="h-[18px] w-[18px]" />
              {count > 0 && (
                <span
                  data-testid="nav-cart-count"
                  className="absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-sage-500 px-1 text-[10px] font-semibold text-white"
                >
                  {count}
                </span>
              )}
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {trackOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={closeTrack}
            className="fixed inset-0 z-[70] flex items-end justify-center bg-charcoal-900/50 backdrop-blur-sm sm:items-center sm:px-5"
            data-testid="nav-track-overlay"
          >
            <motion.div
              initial={{ y: 60, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 60, opacity: 0 }}
              transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md rounded-t-3xl border border-charcoal-900/10 bg-white p-6 pb-9 sm:rounded-3xl sm:pb-6"
              data-testid="nav-track-sheet"
            >
              <div className="flex items-center justify-between">
                <h3 className="font-serif text-2xl text-charcoal-900">Track your order</h3>
                <button
                  onClick={closeTrack}
                  aria-label="Close tracking"
                  data-testid="nav-track-close-btn"
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-charcoal-900/10 bg-oat-100 text-charcoal-500 transition-colors hover:text-charcoal-900"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <p className="mt-1 text-xs text-charcoal-500">Enter your order number — live status right here.</p>

              <form onSubmit={trackSearch} className="mt-4 flex gap-2" data-testid="nav-track-sheet-form">
                <input
                  value={trackVal}
                  onChange={(e) => setTrackVal(e.target.value.toUpperCase())}
                  placeholder="OM-XXXXXXX"
                  data-testid="nav-track-sheet-input"
                  className="w-full min-w-0 flex-1 rounded-full border border-charcoal-900/15 bg-oat-50 px-4 py-3 text-sm text-charcoal-900 outline-none transition-colors focus:border-sage-500"
                />
                <button
                  type="submit"
                  disabled={trackLoading}
                  data-testid="nav-track-sheet-btn"
                  className="shrink-0 rounded-full bg-charcoal-900 px-5 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-oat-100 transition-colors hover:bg-sage-600 disabled:opacity-60"
                >
                  {trackLoading ? "…" : "Track"}
                </button>
              </form>

              {trackErr && (
                <p className="mt-3 rounded-xl bg-gold/10 px-4 py-2.5 text-xs text-[#8a6a2f]" data-testid="nav-track-sheet-error">
                  {trackErr}
                </p>
              )}

              {trackOrder && (
                <div className="mt-4 rounded-2xl bg-oat-100 p-4" data-testid="nav-track-sheet-result">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-serif text-lg text-charcoal-900">{trackOrder.order_number}</p>
                    <span
                      className={`flex items-center gap-1 rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] ${
                        trackOrder.status === "cancelled"
                          ? "bg-[#fdecec] text-[#b91c1c]"
                          : trackOrder.status === "delivered"
                          ? "bg-sage-700 text-white"
                          : ["shipped", "returned"].includes(trackOrder.status)
                          ? "bg-sage-500 text-white"
                          : "bg-oat-200 text-charcoal-700"
                      }`}
                      data-testid="nav-track-sheet-status"
                    >
                      {trackOrder.status === "cancelled" ? <XCircle className="h-3 w-3" /> : ["shipped", "returned"].includes(trackOrder.status) ? <Truck className="h-3 w-3" /> : <Package className="h-3 w-3" />}
                      {trackOrder.status}
                    </span>
                  </div>

                  <div className="mt-3 flex items-center gap-1.5">
                    {["Placed", "Shipped", "Delivered"].map((label, idx) => {
                      const done = trackOrder.status === "cancelled"
                        ? false
                        : idx === 0 || (idx === 1 && ["shipped", "delivered", "returned"].includes(trackOrder.status)) || (idx === 2 && trackOrder.status === "delivered");
                      return (
                        <div key={label} className="flex flex-1 items-center gap-1.5">
                          <span
                            className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] ${
                              done ? "bg-sage-500 text-white" : "border border-charcoal-900/15 bg-white text-charcoal-500/50"
                            }`}
                          >
                            {done ? <Check className="h-3 w-3" /> : idx + 1}
                          </span>
                          <span className="hidden text-[10px] font-medium uppercase tracking-wider text-charcoal-500 sm:block">{label}</span>
                          {idx < 2 && <span className={`h-px flex-1 ${idx < (done ? 1 : 0) ? "bg-sage-400" : "bg-charcoal-900/10"}`} />}
                        </div>
                      );
                    })}
                  </div>

                  <p className="mt-3 text-xs text-charcoal-500">
                    {trackOrder.items.reduce((s, i) => s + i.quantity, 0)}× OmniMist™ Sprayer ·{" "}
                    {trackOrder.payment_method === "cod" ? "Cash on Delivery" : "Paid online"} ·{" "}
                    <span className="font-semibold text-charcoal-900">{fmt(trackOrder.total)}</span>
                    {trackOrder.shipment?.awb && <> · AWB {trackOrder.shipment.awb}</>}
                  </p>

                  <button
                    onClick={() => {
                      closeTrack();
                      navigate(`/track?order=${trackOrder.order_number}`);
                    }}
                    data-testid="nav-track-full-link"
                    className="mt-3 w-full rounded-full border border-sage-500/40 bg-sage-100 px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-sage-700 transition-colors hover:bg-sage-200"
                  >
                    Open full tracking
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => setMenuOpen(false)}
            className="fixed inset-0 z-[70] flex items-end justify-center bg-charcoal-900/50 backdrop-blur-sm sm:items-center sm:px-5"
            data-testid="nav-menu-overlay"
          >
            <motion.div
              initial={{ y: 60, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 60, opacity: 0 }}
              transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md rounded-t-3xl border border-charcoal-900/10 bg-white p-6 pb-9 sm:rounded-3xl sm:pb-6"
              data-testid="nav-menu-sheet"
            >
              <div className="flex items-center justify-between">
                <h3 className="font-serif text-2xl text-charcoal-900">Explore OmniMist</h3>
                <button
                  onClick={() => setMenuOpen(false)}
                  aria-label="Close menu"
                  data-testid="nav-menu-close-btn"
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-charcoal-900/10 bg-oat-100 text-charcoal-500 transition-colors hover:text-charcoal-900"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2">
                {NAV_LINKS.map((l) => (
                  <button
                    key={l.id}
                    onClick={() => {
                      setMenuOpen(false);
                      scrollToId(l.id);
                    }}
                    data-testid={`nav-menu-link-${l.id}`}
                    className="rounded-2xl border border-charcoal-900/10 bg-oat-50 px-4 py-3.5 text-left text-sm font-medium uppercase tracking-[0.12em] text-charcoal-700 transition-colors hover:border-sage-500 hover:text-sage-700"
                  >
                    {l.label}
                  </button>
                ))}
              </div>
              <button
                onClick={() => {
                  setMenuOpen(false);
                  navigate("/track?return=1");
                }}
                data-testid="nav-menu-return-btn"
                className="mt-3 flex w-full items-center justify-center gap-2 rounded-full border-2 border-charcoal-900/25 bg-white px-4 py-3.5 text-xs font-semibold uppercase tracking-[0.14em] text-charcoal-700 transition-colors hover:border-sage-500 hover:text-sage-700"
              >
                <RotateCcw className="h-4 w-4" /> Request a return
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </AnimatePresence>
  );
}
